import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormContext } from '@glu/validation-react';
import moment from 'moment';
import gluLocale from '@glu/locale';
import { createUseStyles } from '@glu/theming';
import {
  END_DATE,
  FURTHER_NOTICE,
  ValidatedScheduler,
  defaultStrings_en_US as defaultSchedulerStrings,
  COMPLEX
} from '@glu/scheduler-react';
import { defaultStrings_en_US as defaultDateStrings } from '@glu/datepicker-react';

import { getAllowedTransferDates } from 'api/transfers/service';
import { DATE_FORMAT } from 'constants.js';
import BeforeAfterDayRadio from 'components/beforeAfterDayRadio/BeforeAfterDayRadio';
import usePlaceResolver, { variants } from 'components/usePlaceResolver/usePlaceResolver';
import { calendarMonths } from 'components/locale/dateTime.locale';
import LocaleContext from 'components/locale/LocaleContext';
import { FrequencyType } from 'components/loanPaymentTemplate/types/form/scheduler';

import styles from './recurringTransfer.styles';

const useStyles = createUseStyles(styles);

export const RecurringTransferScheduler = ({ disabled, readOnly }) => {
  const classes = useStyles(styles);
  const locale = useContext(LocaleContext);

  gluLocale.set({
    ...defaultSchedulerStrings,
    ...defaultDateStrings,
    ...calendarMonths,
    'validator.required': locale['validation.required']
  });
  const { data, onChange: setValue } = useContext(FormContext);
  const services = useMemo(() => [getAllowedTransferDates()], []);
  const { Placeholder, results } = usePlaceResolver({
    services,
    variant: variants.input
  });

  const [calendar = {}] = results;

  if (Placeholder) {
    return <Placeholder />;
  }

  const isSelectableDate = (day) => {
    const holidays = calendar?.holidays || [];
    const businessDaysOfWeek = calendar?.businessDaysOfWeek || [];
    const isHoliday = holidays.some((d) => day.isSame(moment(d), 'day'));

    if (isHoliday) {
      return true;
    }
    if (!businessDaysOfWeek[day.day()]) {
      return true;
    }
    return false;
  };

  // We don't (currently) support 'Day of Week' dropdowns for semi monthly
  const semiMonthlyDowHack = (classes) => (data) =>
    [data?.recurring?.type, data?.recurring?.value?.type].includes(FrequencyType.SEMIMONTHLY)
      ? classes.semiMonthly
      : '';

  const getEndsComplexOptions = () => {
    if (readOnly) {
      const { endValue } = data?.scheduling || {};
      return [endValue ? END_DATE : FURTHER_NOTICE];
    }
    return [END_DATE, FURTHER_NOTICE];
  };

  return (
    <>
      <ValidatedScheduler
        {...data?.scheduling}
        className={classNames(classes.schedulerWeeklyFix, semiMonthlyDowHack(classes)(data))}
        daily={false}
        dataQa="recurring-transfer"
        dateFormat={DATE_FORMAT}
        disabled={disabled}
        DatePickerProps={{
          autoApply: true,
          dateMin: moment.max(moment().add(1, 'day'), moment(calendar.earliestDayForTransfer)),
          isSelectableDate
        }}
        ends={COMPLEX}
        endsComplexOptions={getEndsComplexOptions()}
        htmlId="recurring-transfer"
        name="recurring"
        starts
        monthly={{
          canSetTime: false,
          enabled: true,
          multipleDays: false
        }}
        weekly={{
          allowWeekend: true,
          canSetTime: false,
          enabled: true,
          minDays: 1
        }}
        semiMonthly={{
          canSetTime: false,
          enabled: true,
          multipleDays: true
        }}
        validators={[
          {
            msg: locale['validation.required'],
            required: true
          }
        ]}
      />
      <BeforeAfterDayRadio futureDated={data?.futureDated} setInitial onChange={setValue} />
    </>
  );
};

RecurringTransferScheduler.propTypes = {
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool.isRequired
};

RecurringTransferScheduler.defaultProps = {
  disabled: false
};

export default RecurringTransferScheduler;
